import { useSyncExternalStore } from 'react'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'

/* ------------------------------------------------------------------ */
/*  Global keyboard-height store (iOS only)                            */
/*                                                                     */
/*  The app runs with Keyboard.resize: "none", so the WKWebView frame  */
/*  never changes when the keyboard shows — no full-page relayout, no  */
/*  vh/dvh jumps, no flash. Instead this module publishes the current  */
/*  keyboard height and the UI compensates with animatable CSS:        */
/*  bottom sheets lift by the height, scroll areas pad their bottom.   */
/*                                                                     */
/*  Consumers: the `--keyboard-height` CSS var (set on <html>) for     */
/*  plain-CSS compensation, `useKeyboardHeight()` for React layout,    */
/*  and `subscribeKeyboard`/`getKeyboardHeight` for non-React code.    */
/*  Off iOS nothing attaches, the var stays unset and the hook         */
/*  returns 0, so all compensation styles resolve to no-ops.           */
/* ------------------------------------------------------------------ */

let keyboardHeight = 0
const subscribers = new Set<() => void>()

const notify = () => subscribers.forEach((fn) => fn())

function setHeight(px: number) {
  if (keyboardHeight === px) return
  keyboardHeight = px
  document.documentElement.style.setProperty('--keyboard-height', `${px}px`)
  notify()
}

/** Attach the native keyboard listeners. Call once at startup (main.tsx). */
export function initKeyboard() {
  if (Capacitor.getPlatform() !== 'ios') return

  void Keyboard.addListener('keyboardWillShow', (info) => {
    setHeight(info.keyboardHeight)
    // The webview no longer resizes for the keyboard, so bring the focused
    // field into view ourselves once the lift transitions have settled.
    setTimeout(() => {
      const active = document.activeElement
      if (active instanceof HTMLElement) {
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }, 350)
  })

  void Keyboard.addListener('keyboardWillHide', () => {
    setHeight(0)
  })

  // WebKit sometimes force-scrolls the page to expose an obscured field even
  // though the document itself is overflow-hidden; undo it — visibility is
  // handled by the lift styles + scrollIntoView above.
  void Keyboard.addListener('keyboardDidShow', () => {
    if (window.scrollY !== 0) window.scrollTo(0, 0)
  })
}

export const getKeyboardHeight = () => keyboardHeight
export const isKeyboardVisible = () => keyboardHeight > 0

export function subscribeKeyboard(fn: () => void): () => void {
  subscribers.add(fn)
  return () => {
    subscribers.delete(fn)
  }
}

/** Current keyboard height in px; 0 while hidden and always 0 off iOS. */
export function useKeyboardHeight(): number {
  return useSyncExternalStore(subscribeKeyboard, getKeyboardHeight, () => 0)
}
